import { Link, useLocation } from 'react-router-dom';
import {
    Grid,
    Typography,
    Button,
    IconButton,
    Menu,
    MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import innoIcon from './innoIconWhite.png';
import MonitorIcon from '@mui/icons-material/Monitor';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import PeopleIcon from '@mui/icons-material/People';
import VerticalSplitIcon from "@mui/icons-material/VerticalSplit";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import DataObjectIcon from '@mui/icons-material/DataObject';
import { theme as muiTheme } from '../components/muiTheme';
import InnoTooltip from '../components/InnoTooltip';
import useCheckPermission from '../helpers/checkPermission';
import { useAuthStore } from '../contexts/AuthContext';
import { useAccountStore } from '../contexts/AccountContext';
import { useEffect, useState, useMemo } from 'react';
import { useNavDispatch, useNavStore } from '../contexts/NavContext';
import { useCreateDashboard } from '../apiCalls/dashboards';
import { useWorkspaceDashboardSelection } from '../pages/workspace/nav/workspaceNavigationHooks';
import { useModalDispatch } from '../contexts/ModalContext';
import { useSelectedDashboardDispatch } from '../contexts/SelectedDashboardContext';
import { useDashboardListStore } from '../contexts/DashboardListContext';
import { useTrack } from '../analytics/hooks';
import { DashboardSlotA } from '../contexts/DashboardSlots';
import { useBannerDispatch} from '../contexts/BannerContext';

export default function Sidebar() {
    const [pages, setPages] = useState([]);
    const { pathname } = useLocation();
    const [formatPath, setFormatPath] = useState();
    const [currentIndex, setCurrentIndex] = useState();
    const { permitted: accountPerm } = useCheckPermission('navigation.account.all');
    const { permitted: userPerm } = useCheckPermission('navigation.user.all');
    const { user, selectedAccount } = useAuthStore();
    const { accounts } = useAccountStore();
    const navDispatch = useNavDispatch();
    const selectedDashDispatch = useSelectedDashboardDispatch();
    const { isMainSideBarExpanded, isMainSideBarHovered } = useNavStore();
    const { createDashboard } = useCreateDashboard();
    const setDashboard = useWorkspaceDashboardSelection();
    const modalDispatch = useModalDispatch();
    const [anchorEl, setAnchorEl] = useState(null);
    const [optionList, setOptionList] = useState([]);
    const { dashboards } = useDashboardListStore();
    const track = useTrack();
    const bannerDispatch = useBannerDispatch();
    const dashboardGlobalDataADispatch = DashboardSlotA.useDataDispatch();
    const tooltipStyle =
    {
        "& .MuiTooltip-tooltip": {
            fontSize: "14px",
            fontWeight: 700,
            fontFamily: "Lato",
            margin: "10px",
            backgroundColor: "#AC5CCC",
        },
        "& .MuiTooltip-arrow": {
            color: "#AC5CCC",
        },
        color: "#AC5CCC",
    }

    const { permitted: dashboardPerm } = useCheckPermission(
        "dashboard.create.all"
    );
    const open = Boolean(anchorEl);

    //if the login user is superAdmin, then it has everything
    //otherwise check if the selectedAccount has settings view-marketplace, if yes, then show the marketplace, if not, not shown
    //if the user does not have account.view.all then do not show the accouts tab
    //if the user does not have the user.view.all then do not show the users tab
    useEffect(() => {
        if (!selectedAccount) return
        let tmpPages;
        if (user.isSuperAdmin) {
            const notProdEnvironment = process.env.REACT_APP_INNO_ENV !== "production";
            const addDataBuilder = { path: "/data-builder", icon: DataObjectIcon, title: "Build Data" }
            tmpPages = [
                { path: "/workspace", icon: MonitorIcon, title: "Workspace" },
                { path: "/marketplace", icon: ViewQuiltIcon, title: "Templates" },
                { path: `/user-management/${selectedAccount.uniqueHash}/users`, icon: PeopleIcon, title: "Users" },
                { path: `/user-management/${selectedAccount.uniqueHash}/accounts`, icon: AdminPanelSettingsIcon, title: "Accounts" },
            ];
            if (notProdEnvironment) {
                tmpPages.push(addDataBuilder);
            }
        } else {
            tmpPages = [{ path: "/workspace", icon: MonitorIcon, title: "Workspace" }];
            if (Object.keys(accounts).length === 0) return;
            const settingsInAccount = accounts[selectedAccount.id]["AccountSettings"].map((accountsetting) => accountsetting.Setting.slug);
            if (settingsInAccount.includes('view-marketplace')) {
                tmpPages.push({ path: "/marketplace", icon: ViewQuiltIcon, title: "Templates" });
            }
            if (userPerm) {
                tmpPages.push({ path: `/user-management/${selectedAccount.uniqueHash}/users`, icon: PeopleIcon, title: "Users" });
            }
            if (accountPerm) {
                tmpPages.push({ path: `/user-management/${selectedAccount.uniqueHash}/accounts`, icon: AdminPanelSettingsIcon, title: "Accounts" });
            }
        }
        setPages(tmpPages)
    }, [accounts, user, selectedAccount, accountPerm, userPerm]);

    useEffect(() => {
        const splitPath = pathname.split("/")
        let tmpPathName

        if (splitPath[1] === "workspace" && !splitPath[2]) {//if click on the workspace, then clean the selected dashbaord id so the breeadcrumbe go to workspace
            selectedDashDispatch({ type: "setSelectedDashboardId", payload: null });
        }

        if (splitPath[1] === "user-management") {
            tmpPathName = `/${splitPath[1]}/${splitPath[2]}/${splitPath[3]}`;
        } else {
            tmpPathName = `/${splitPath[1]}`;
        }
        setFormatPath(tmpPathName)
        const tmpindex = pages.findIndex((p) => p.path === tmpPathName);
        setCurrentIndex(tmpindex)
        //set up the selected option in the store to update the toggle selection on the user-management page
        if (pages[tmpindex]?.title === "Users" || pages[tmpindex]?.title === "Accounts") {
            if (pages[tmpindex]?.title === "Users") {
                track("users_tab_visit", {
                    userFullName: user.fullName,
                    userEmail: user.email,
                    accountId: selectedAccount.id,
                    accountName: selectedAccount.name,
                    actionTime: Date()
                });
            }
            navDispatch({ type: "changeOption", payload: pages[tmpindex].title });
        }
    }, [pages, pathname])

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const reachedDashboardLimit = useMemo(() => {
        if (!selectedAccount) return false;
        const dashboardLimit = accounts[selectedAccount.id]?.AccountLimits?.find((limit) => limit.Limit.slug === "nativeDashboards");
        if (!dashboardLimit || dashboardLimit?.unlimited) return false;
        //get current dashboards in the selected account
        const accountDashboards = Object.values(dashboards).filter((dash) => {
            return dash.AccountId === selectedAccount.id && !dash.embedUrl;
        });
        return accountDashboards?.length >= dashboardLimit?.amount;
    }, [selectedAccount, accounts, dashboards]);

    async function handleNewDashboard() {
        try {
            dashboardGlobalDataADispatch({ type: "clearAllData" });
            setAnchorEl(null);
            if (user?.isSuperAdmin && selectedAccount?.id) {//if the user is super admin and the account is not the inno account
                modalDispatch({
                    type: "openModal",
                    payload: "assignOwnership",
                });
            } else {
                async function getNewDashboard() {
                    let initialDash = {
                        title: "Untitled Dashboard",
                        AccountId: selectedAccount.id,
                        UserId: user.id,
                        description: "",
                        layout: { rows: [], drafts: {} }
                    };
                    const newDashboard = await createDashboard({
                        newDashboardInfo: initialDash,
                        event: "dashboard_create"
                    });
                    if (newDashboard?.reachedLimit) {
                        bannerDispatch({
                            type: "changeBannerState",
                            payload: {
                                mainText: "You have reached this account's limit for native dashboards",
                                subText: "Please delete a dashboard to create a new one",
                                tallBanner: true,
                                success: false,
                            },
                        });
                        return;
                    }
                    return newDashboard;
                }
                const dashboard = await getNewDashboard();
                setDashboard(dashboard, "/edit");
            }
        } catch (error) {
            console.error("Error creating new dashboard:", error);
        }
    }

    function handleEmbeddedDashboard() {
        modalDispatch({
            type: "openModal",
            payload: "createEmbeddedDashboard",
        });
    }

    useEffect(() => {
        if (!selectedAccount) return
        let tmpOption
        if (user.isSuperAdmin) {//Super Admin has permissions for create native and embed dash no matter which accounts they are on
            tmpOption = [
                {
                    name: "New Dashboard",
                    handleFunc: handleNewDashboard,
                },
                {
                    name: "Embedded Dashboard",
                    handleFunc: handleEmbeddedDashboard,
                },
            ];
        } else {
            const permissions = accounts[selectedAccount.id]?.AccountSettings.map((s) => s.Setting.slug);//get all account settings in this account
            if (permissions?.includes("create-dashboards") && dashboardPerm) {
                tmpOption = [{
                    //users can create native dash if they have dashboard.create.all permission and the account has native dash setting
                    name: "New Dashboard",
                }];
            } else {
                tmpOption = []
            }
        }
        setOptionList(tmpOption)
    }, [selectedAccount, accounts]);

    useEffect(() => {//when first loading on the page, check if the login user has created a dash before, if yes, then unexpand the sidebar, if no, expand the sidebar
        if (Object.values(dashboards).length > 0 && Object.values(dashboards).find(dash => dash.UserId === user.id)) {
            navDispatch({ type: "changeMainSideBarExpand", payload: false })
        } else {
            navDispatch({ type: "changeMainSideBarExpand", payload: true })
        }
    }, [Object.values(dashboards).length])

    return (
        <Grid
            className='workspace-big-side-bar'
            data-testid="workspace-sidebar"
            container
            direction="column"
            alignItems="center"
            onMouseEnter={() => navDispatch({ type: "changeHoaverOnMainSideBar", payload: true })}
            onMouseLeave={() => {
                setAnchorEl(null)
                navDispatch({ type: "changeHoaverOnMainSideBar", payload: false })
            }}
            width={isMainSideBarHovered || isMainSideBarExpanded ? "150px" : "80px"}
            height="100%"
            minHeight="680px"
        >
            <Grid item sx={{ height: "55px", width: "100%", paddingTop: "12px", paddingLeft: "26px" }}>
                <img src={innoIcon} alt="logo" height="40px" width="28.83px" />
            </Grid>

            {isMainSideBarExpanded ?
                <Grid item sx={{ height: "24px", width: "100%", paddingLeft: "120px" }}>
                    <KeyboardArrowLeftIcon
                        sx={{ color: 'white !important', fontSize: 30, cursor: 'pointer' }}
                        onClick={() => {
                            navDispatch({ type: "changeMainSideBarExpand", payload: false })
                            navDispatch({ type: "changeHoaverOnMainSideBar", payload: false })
                        }}
                    />
                </Grid> :
                (
                    isMainSideBarHovered ?
                        <Grid item sx={{ height: "24px", width: "100%", paddingLeft: "120px" }}>
                            <VerticalSplitIcon
                                sx={{ color: 'white !important', fontSize: 30, cursor: 'pointer' }}
                                onClick={() => {
                                    navDispatch({ type: "changeHoaverOnMainSideBar", payload: false });
                                    navDispatch({ type: "changeMainSideBarExpand", payload: true })
                                }}
                            />
                        </Grid> :
                        <Grid item sx={{ height: "24px", width: "100%" }} />
                )
            }

            <Grid item width="100%" marginTop="11px">
                <Grid container direction="column" alignItems="flex-end" justifyContent="flex-start" height='100%'>
                    <SidebarCurvySpace className="sidebar-curvy-space" pages={pages} position='top' currentIndex={currentIndex} />
                    {
                        pages.map(page => {
                            return (
                                <SidebarLink
                                    key={page.title}
                                    pages={pages}
                                    page={page}
                                    currentIndex={currentIndex}
                                    path={formatPath}
                                    pageLength={pages.length}
                                    isMainSideBarExpanded={isMainSideBarExpanded}
                                    isMainSideBarHovered={isMainSideBarHovered}
                                />
                            )
                        })
                    }
                    <SidebarCurvySpace position='bottom' pages={pages} currentIndex={currentIndex} />
                </Grid>
            </Grid>
            <Grid
                item
                sx={{
                    width: isMainSideBarExpanded || isMainSideBarHovered ? "141px" : "40px",
                    height: "40px",
                    // padding: "5px"
                }}
            >
                {optionList.length > 0 && (
                    <>
                        {isMainSideBarExpanded || isMainSideBarHovered ? (
                            <InnoTooltip title={reachedDashboardLimit ? "Maximum dashboards created. Please delete one or contact support to increase limit. " : ""} placement="right" sxStyle={tooltipStyle}>
                                <Button
                                    variant="contained"
                                    onClick={optionList.length > 1 ? handleClick : handleNewDashboard}
                                    disabled={reachedDashboardLimit}
                                    sx={{
                                        textTransform: 'none',
                                        background: "#AC5CCC",
                                        borderRadius: "20px",
                                        opacity: 1,
                                        padding: "5px",
                                        justifyContent: "space-between",
                                        width: "100%",
                                        height: "100%",
                                        '&:hover': {
                                            backgroundColor: '#AC5CCC', // Maintain the initial background color on hover
                                        },
                                        "&:disabled": {
                                            backgroundColor: "var(--Neutrals-900, #6F8090)", // Adjust to your preferred disabled color
                                            color: "var(--Neutrals-900, #6F8090)",
                                            opacity: 0.5
                                        },
                                    }}
                                >
                                    <AddIcon sx={{ fontSize: 20 }} />
                                    <Typography
                                        sx={{
                                            color: "var(--Neutrals-100, #FFF)",
                                            textAlign: "center",
                                            fontFamily: "Lato",
                                            fontSize: "14px",
                                            fontStyle: "normal",
                                            fontWeight: "700",
                                            lineHeight: "normal",
                                            paddingRight: "5px"
                                        }}
                                    >
                                        New Dashboard
                                    </Typography>
                                </Button>
                            </InnoTooltip>
                        ) : (
                            <InnoTooltip title={reachedDashboardLimit ? "Maximum dashboards created. Please delete one or contact support to increase limit. " : ""} placement="right" sxStyle={tooltipStyle}>
                                <IconButton
                                    onClick={optionList.length > 1 ? handleClick : handleNewDashboard}
                                    disabled={reachedDashboardLimit}
                                    variant="solid"
                                    sx={{
                                        backgroundColor: "#AC5CCC",
                                        width: "100%",
                                        height: "100%",
                                        padding: "5px",
                                        '&:hover': {
                                            backgroundColor: '#AC5CCC', // Maintain the initial background color on hover
                                        },
                                        "&:disabled": {
                                            backgroundColor: "var(--Neutrals-900, #6F8090)", // Adjust to your preferred disabled color
                                            color: "var(--Neutrals-900, #6F8090)",
                                            opacity: 0.5
                                        },
                                    }}
                                >
                                    <AddIcon sx={{ fontSize: 20, color: "#FFFFFF" }} />
                                </IconButton>
                            </InnoTooltip>
                        )}
                        {optionList.length > 1 && <Menu //only show the two option when can create both native and embed dash
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            anchorOrigin={{
                                vertical: 'center',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'center',
                                horizontal: 'left',
                            }}
                            MenuListProps={{
                                sx: {
                                    padding: "3px 0px 0px 0px",
                                }
                            }}
                            PaperProps={{
                                sx: {
                                    padding: "0px",
                                }
                            }}
                        >
                            {optionList.map((option, index) => (
                                <MenuItem
                                    key={index}
                                    onClick={option.handleFunc}
                                    sx={{
                                        paddingTop: "1px",
                                        paddingBottom: "4px",
                                        '&:hover': {
                                            backgroundColor: '#EAF9FF',
                                        }
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            textAlign: "left",
                                            font: "normal normal bold 14px/17px Lato",
                                            letterSpacing: "0px",
                                            color: "#161A1D",
                                            opacity: "1"
                                        }}
                                    >
                                        {option.name}
                                    </Typography>
                                </MenuItem>
                            ))}
                        </Menu>}
                    </>
                )}
            </Grid>
        </Grid>
    )
}

const linkSpaceStyle = {
    height: '100%',
    width: '100%',
}

function SidebarCurvySpace({ pages, children, position, on, currentIndex }) {
    return (
        <Grid item height='55px' width='90%'>
            <Grid container direction="row"
                alignItems="center"
                justifyContent="center"
                height='100%'
                width='100%'
                sx={{
                    bgcolor: on ? muiTheme.palette.primary.main : muiTheme.palette.white.secondary
                }}
            >
                {position ? <div
                    style={{
                        ...linkSpaceStyle,
                        backgroundColor: muiTheme.palette.primary.main,
                        borderRadius: `0px ${position === 'bottom' && pages.length - 1 === currentIndex ? '20px' : '0px'} ${position === 'top' && currentIndex === 0 ? '20px' : '0px'} 0px`,
                    }}
                /> : children}
            </Grid>
        </Grid>
    )
}

function SidebarLink({ pages, path, currentIndex, page, pageLength, isMainSideBarExpanded, isMainSideBarHovered }) {
    const on = page.path === path;
    const Icon = page.icon;

    //this part takes care of the condition that if there are only two tabs, curve would still work
    let before
    let after
    if (pageLength === 2) {
        if (page.path !== path) {
            if (currentIndex === 0) {
                after = true
                before = false
            }
            else {
                after = false
                before = true
            }

        }
    } else {
        before = (currentIndex !== 0 && currentIndex !== -1) && page.path === pages[currentIndex - 1]?.path;
        after = (currentIndex !== pages.length - 1 && currentIndex !== -1) && page.path === pages[currentIndex + 1]?.path
    }

    return (
        <SidebarCurvySpace on={on}>
            <Link style={{
                color: 'inherit',
                textDecoration: 'inherit',
                ...linkSpaceStyle,
                borderRadius: `${on ? '20px' : '0px'} ${after ? '20px' : '0px'} ${before ? '20px' : '0px'} ${on ? '20px' : '0px'}`,
                backgroundColor: on ? muiTheme.palette.white.secondary : muiTheme.palette.primary.main,
            }}
                to={page.path}
            >
                <IconButton sx={{
                    color: on ? muiTheme.palette.secondary.main : muiTheme.palette.white.secondary,
                    height: "100%",
                }}
                    size='large'
                >
                    <Grid container direction="row" sx={{ justifyContent: "space-between", alignItems: "center", paddingTop: "7px" }}>
                        <InnoTooltip placement="right" >
                            <Icon fontSize='inherit' />
                        </InnoTooltip>
                        {(isMainSideBarHovered || isMainSideBarExpanded) && <Typography sx={{ paddingLeft: "11.6px", paddingBottom: "7px" }}>{page.title}</Typography>}
                    </Grid>
                </IconButton>
            </Link>
        </SidebarCurvySpace>
    )
}